import { useEffect, useRef } from 'react'
import { cn } from '@/utils/cn'
import type { Quaternion } from '@/utils/quaternion'
import { quaternionToEuler } from '@/utils/quaternion'

interface AxisHelperProps {
  className?: string
  cameraRotation?: [number, number, number] // Legacy support
  cameraQuaternion?: Quaternion // New quaternion prop
  size?: number
}

export function AxisHelper({ className, cameraRotation, cameraQuaternion, size = 80 }: AxisHelperProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    canvas.width = size
    canvas.height = size
    
    // Clear canvas
    ctx.clearRect(0, 0, size, size)

    // Get camera rotation - convert from quaternion if provided, otherwise use legacy rotation
    let rotation: [number, number, number]
    if (cameraQuaternion) {
      rotation = quaternionToEuler(cameraQuaternion)
    } else {
      rotation = cameraRotation || [0, 0, 0]
    }
    const [phi, theta] = rotation

    // Calculate 3D to 2D projection for each axis
    const center = size / 2
    const axisLength = size * 0.35

    // Add background circle for better visibility
    ctx.globalAlpha = 0.8
    ctx.beginPath()
    ctx.arc(center, center, center - 5, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
    ctx.fill()
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
    ctx.lineWidth = 1
    ctx.stroke()

    // Define axis vectors in 3D space
    const axes = [
      { dir: [1, 0, 0], color: '#ff4444', label: 'X' }, // Red X
      { dir: [0, 1, 0], color: '#44ff44', label: 'Y' }, // Green Y  
      { dir: [0, 0, 1], color: '#4444ff', label: 'Z' }  // Blue Z
    ]

    // Transform and render each axis
    axes.forEach(axis => {
      const [x, y, z] = axis.dir

      // Apply camera rotation to get screen coordinates
      // Rotate around Y axis (theta), then around X axis (phi)
      const cosTheta = Math.cos(-theta)
      const sinTheta = Math.sin(-theta)
      const cosPhi = Math.cos(-phi)
      const sinPhi = Math.sin(-phi)

      // First rotate around Y axis
      const x1 = x * cosTheta - z * sinTheta
      const y1 = y
      const z1 = x * sinTheta + z * cosTheta

      // Then rotate around X axis
      const x2 = x1
      const y2 = y1 * cosPhi - z1 * sinPhi
      const z2 = y1 * sinPhi + z1 * cosPhi

      // Project to 2D (orthographic projection)
      const screenX = center + x2 * axisLength
      const screenY = center - y2 * axisLength // Flip Y for screen coordinates

      // Only draw if pointing towards camera (z2 > 0 means away from camera)
      const alpha = z2 < 0 ? 1 : 0.3 // Dim if pointing away

      // Draw axis line
      ctx.beginPath()
      ctx.moveTo(center, center)
      ctx.lineTo(screenX, screenY)
      ctx.strokeStyle = axis.color
      ctx.globalAlpha = alpha
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.stroke()

      // Draw axis label
      ctx.font = 'bold 14px sans-serif'
      ctx.fillStyle = axis.color
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      
      // Position label slightly beyond the axis end
      const labelX = center + x2 * (axisLength + 15)
      const labelY = center - y2 * (axisLength + 15)
      ctx.fillText(axis.label, labelX, labelY)
    })

    // Draw center dot
    ctx.globalAlpha = 1
    ctx.beginPath()
    ctx.arc(center, center, 3, 0, Math.PI * 2)
    ctx.fillStyle = '#666'
    ctx.fill()

  }, [cameraRotation, cameraQuaternion, size])

  return (
    <div className={cn('absolute bottom-4 right-2 pointer-events-none', className)}>
      <canvas
        ref={canvasRef}
        className="rounded-full border border-white/20"
        style={{ width: size, height: size }}
      />
    </div>
  )
}